import api from "./api";
import { uploadSegmentDirect } from "./directUpload";

// ── Teacher: Exam CRUD ──

export const createExam = async (courseId, data) => {
  const res = await api.post(`/exam/courses/${courseId}/exams`, data);
  return res.data;
};

export const getCourseExams = async (courseId, params = {}) => {
  const res = await api.get(`/exam/courses/${courseId}/exams`, { params });
  return res.data;
};

// The tests this faculty member is on by the roster — evaluator or proctor —
// usually of a course they do not teach (the bulk schedule records them):
// { exams: [{ _id, title, status, scheduledStartTime, course, roles }] }.
export const getMyRosteredExams = async () => {
  const res = await api.get("/exam/teacher/rostered-exams");
  return res.data;
};

export const getStudentAllExams = async () => {
  const res = await api.get("/exam/student/my-exams");
  return res.data;
};

export const getExamById = async (examId) => {
  const res = await api.get(`/exam/exams/${examId}`);
  return res.data;
};

export const updateExam = async (examId, data) => {
  const res = await api.put(`/exam/exams/${examId}`, data);
  return res.data;
};

export const deleteExam = async (examId) => {
  const res = await api.delete(`/exam/exams/${examId}`);
  return res.data;
};

export const publishExam = async (examId) => {
  const res = await api.post(`/exam/exams/${examId}/publish`);
  return res.data;
};

export const cancelExam = async (examId) => {
  const res = await api.post(`/exam/exams/${examId}/cancel`);
  return res.data;
};

// ── Student: Exam Taking ──

export const getExamForStudent = async (examId) => {
  const res = await api.get(`/exam/exams/${examId}/take`);
  return res.data;
};

// body: { accepted: true } once the candidate ticked the test's statement in
// the lobby (3.6) — the server refuses a NEW attempt without it when the exam
// asks (ACCEPTANCE_REQUIRED); a resume needs nothing.
export const startExam = async (examId, body) => {
  const res = await api.post(`/exam/exams/${examId}/start`, body);
  return res.data;
};

// Panel 2, P6 — the candidate's answers to the institution's privacy notice,
// one per purpose: { answers: { webcam: true, screen: false, … },
// alternativeRoute?: true }. Before Start (the lobby) and during the exam
// (the Privacy panel: a withdrawal) alike; returns the new state.
export const answerPrivacyConsent = async (examId, body) => {
  const res = await api.post(`/exam/exams/${examId}/privacy-consent`, body);
  return res.data;
};

export const saveAnswer = async (examId, data) => {
  const res = await api.put(`/exam/exams/${examId}/answer`, data);
  return res.data;
};

/**
 * P2 — send a batch of journal rows. Each item carries the client's `seq`;
 * the server keeps the newest per question. Throws on network/HTTP failure
 * so the journal can retry.
 */
export const syncAnswers = async (examId, items) => {
  const res = await api.put(`/exam/exams/${examId}/answers`, { items }, { timeout: 15000 });
  return res.data;
};

/** Secure client (docs/06-LOCKDOWN.md): a challenge for this attempt. */
export const getLockdownNonce = async (examId) => {
  const res = await api.get(`/exam/exams/${examId}/lockdown-nonce`);
  return res.data;
};

/** Secure client: post the desktop client's signed answer to the challenge. */
export const postLockdownAttest = async (examId, body) => {
  const res = await api.post(`/exam/exams/${examId}/lockdown-attest`, body);
  return res.data;
};

/** Secure client (lockdown v2): the app's re-scan of this machine changed — for the proctor. */
export const postLockdownFindings = async (examId, scan) => {
  const res = await api.post(`/exam/exams/${examId}/lockdown-findings`, { scan });
  return res.data;
};

/** Submit, optionally carrying the journal's final snapshot. Idempotent server-side. */
export const submitExam = async (examId, answers) => {
  const res = await api.post(`/exam/exams/${examId}/submit`, answers?.length ? { answers } : {});
  return res.data;
};

export const getMySubmission = async (examId) => {
  const res = await api.get(`/exam/exams/${examId}/my-submission`);
  return res.data;
};

// ── Student: Proctoring ──

export const sendHeartbeat = async (examId, data = {}) => {
  const res = await api.post(`/exam/exams/${examId}/heartbeat`, data);
  return res.data;
};

export const logViolation = async (examId, data) => {
  const res = await api.post(`/exam/exams/${examId}/violation`, data);
  return res.data;
};

export const uploadProctoringScreenshot = async (examId, blob, thumb = null) => {
  const formData = new FormData();
  formData.append("screenshot", blob, "screenshot.jpg");
  // P3 — the Proctor Grid tile's small copy, in the same request.
  if (thumb) formData.append("thumb", thumb, "thumb.jpg");
  const res = await api.post(
    `/exam/exams/${examId}/proctoring-screenshot`,
    formData
  );
  return res.data;
};

/**
 * G13 — one segment of this device's webcam recording
 * (pages/Exam/offline/proctorRecorder.js, docs/15-RECORDING.md). Throws on
 * failure; the recorder decides between retry, drop and stop by the status
 * and `code`. `clientSentAt` lets the server correct this device's clock.
 */
// W5.1 — this page load's memory of whether segments go straight to S3
// (services/directUpload.js): off once the server says DIRECT_OFF or S3 keeps
// refusing; the upload route below is then the only way, as before.
const directRecording = { off: false, failures: 0 };

export const uploadRecordingChunk = async (examId, { blob, seq, startedAt, durationMs, mimeType }) => {
  // The bare type: "video/webm;codecs=vp8,opus" reaches the server's
  // multipart parser as text/plain and would be refused.
  const type = String(mimeType || blob?.type || "video/webm").split(";")[0].trim() || "video/webm";
  // Straight to S3 when the server keeps files there; null means "use the upload route".
  const direct = await uploadSegmentDirect(api, examId, { blob, seq, startedAt, durationMs, type }, directRecording);
  if (direct) return direct;
  const form = new FormData();
  form.append("chunk", new Blob([blob], { type }), `seg-${seq}.${type === "video/mp4" ? "mp4" : "webm"}`);
  form.append("seq", String(seq));
  form.append("startedAt", String(startedAt));
  form.append("durationMs", String(durationMs));
  form.append("clientSentAt", String(Date.now()));
  const res = await api.post(`/exam/exams/${examId}/recording-chunks`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    // A megabyte over a hall's shared uplink can take a while; a hung
    // request must still end so the next retry can happen.
    timeout: 120000,
  });
  return res.data;
};

// ── Student: Activity log (Exam V2 spec §2.6) ──

/**
 * Append a timeline event to the student's in-progress ExamSubmission
 * activity log. Fire-and-forget: network errors are swallowed so a
 * transient issue never interrupts the exam UI. Throttled and
 * whitelisted server-side.
 *
 * @param {String} examId
 * @param {Object} entry
 * @param {String} entry.eventType   one of the server whitelist
 * @param {String} [entry.message]
 * @param {String} [entry.questionRef]
 * @param {Number} [entry.questionIndex]
 * @param {Object} [entry.payload]
 * @param {String} [entry.severity]  info | warning | error | success | critical
 * @returns {Promise<{ success: boolean, entry?: Object }|null>}
 */
export const logActivity = async (examId, entry) => {
  try {
    const res = await api.post(`/exam/exams/${examId}/activity`, entry);
    return res.data;
  } catch (_err) {
    // Intentionally silent: we never want activity logging to break
    // the student exam experience. Server-side rate limits and
    // validation are the source of truth.
    return null;
  }
};

/**
 * Proctoring violation → activity log mirror.
 *
 * The proctoring engine already calls logViolation() which populates
 * ExamSubmission.proctoring.violations[]. This helper additionally
 * mirrors the same event into ExamSubmission.activityLog[] so the
 * User Activity Timeline (spec §2.6) has a unified chronological view
 * across navigation events and proctoring incidents.
 *
 * Human-readable messages are derived client-side so the future modal
 * can render them without another lookup.
 *
 * Fire-and-forget — this helper itself swallows errors by delegating
 * to logActivity which is already silent on failure.
 *
 * @param {String} examId
 * @param {Object} violation  raw object from ProctoringEngine
 *   .onViolation callback ({ type, details })
 */
export const logProctoringViolationActivity = (examId, violation) => {
  if (!examId || !violation || !violation.type) return Promise.resolve(null);

  // Map raw engine types → human-readable messages that the Trust
  // Report and Activity Timeline can show verbatim.
  const MESSAGES = {
    no_face: "Face not visible to the camera",
    multiple_faces: "Multiple faces detected in frame",
    tab_switch: "Student switched tabs or windows",
    copy_paste: "Copy-paste action blocked",
    fullscreen_exit: "Student exited full-screen mode",
    vm_detected: "Virtual machine environment detected",
    earphone_detected: "Earphones / headphones detected",
    devtools_detected: "Developer tools may be open (or the page is zoomed)",
    photo_spoofing: "Photo / image spoof suspected (liveness)",
    gaze_deviation: "Student gaze deviated from screen",
    phone_detected: "Mobile phone detected in view",
    prohibited_object: "Prohibited object detected",
    person_behind: "A second person appeared behind the student",
    new_page_attempt: "Attempted to open a new tab or window",
    second_display: "A second display was connected (secure client)",
  };

  const SEVERITIES = {
    no_face: "warning",
    multiple_faces: "warning",
    tab_switch: "warning",
    copy_paste: "warning",
    fullscreen_exit: "warning",
    vm_detected: "critical",
    earphone_detected: "warning",
    devtools_detected: "warning",
    photo_spoofing: "critical",
    gaze_deviation: "info",
    phone_detected: "warning",
    prohibited_object: "warning",
    person_behind: "warning",
    new_page_attempt: "warning",
    second_display: "critical",
  };

  const eventType = `violation_${violation.type}`;
  const message = MESSAGES[violation.type] || violation.type;
  const severity = SEVERITIES[violation.type] || "warning";

  return logActivity(examId, {
    eventType,
    message,
    severity,
    payload: violation.details ? { details: violation.details } : undefined,
  });
};

// ── Teacher: Grading ──

export const getAllSubmissions = async (examId) => {
  const res = await api.get(`/exam/exams/${examId}/submissions`);
  return res.data;
};

export const gradeSubmission = async (examId, submissionId, data) => {
  const res = await api.put(
    `/exam/exams/${examId}/submissions/${submissionId}/grade`,
    data
  );
  return res.data;
};

export const bulkGradeExam = async (examId, grades) => {
  const res = await api.put(`/exam/exams/${examId}/bulk-grade`, { grades });
  return res.data;
};

export const finalizeGrades = async (examId) => {
  const res = await api.post(`/exam/exams/${examId}/finalize-grades`);
  return res.data;
};

export const flagSubmission = async (examId, submissionId, reason) => {
  const res = await api.post(
    `/exam/exams/${examId}/submissions/${submissionId}/flag`,
    { reason }
  );
  return res.data;
};

export const scheduleReExam = async (examId, data) => {
  const res = await api.post(`/exam/exams/${examId}/re-exam`, data);
  return res.data;
};

// ── Teacher: Analytics ──

export const getExamAnalytics = async (examId) => {
  const res = await api.get(`/exam/exams/${examId}/analytics`);
  return res.data;
};

export const exportResults = async (examId) => {
  const res = await api.get(`/exam/exams/${examId}/export`, {
    responseType: "blob",
  });
  return res.data;
};

// ── Teacher: Live Dashboard ──

export const getLiveDashboard = async (examId) => {
  const res = await api.get(`/exam/exams/${examId}/live-dashboard`);
  return res.data;
};

export const getProctoringReport = async (examId, studentId) => {
  const res = await api.get(
    `/exam/exams/${examId}/proctoring-report/${studentId}`
  );
  return res.data;
};

/**
 * Teacher/admin: record a new ExamObservation against a student
 * (Exam V2 spec §2.3). Types: General | Suspicious | Malpractice.
 *
 * @param {String} examId
 * @param {String} studentId
 * @param {Object} payload
 * @param {String} payload.type
 * @param {String} [payload.note]
 * @param {String[]} [payload.suggestionTags]
 */
export const createExamObservation = async (examId, studentId, payload) => {
  const res = await api.post(
    `/exam/exams/${examId}/observations/${studentId}`,
    payload
  );
  return res.data;
};

/**
 * Teacher/admin: review an ExamObservation — set reviewStatus to
 * either "accepted" or "dismissed" with an optional comment.
 * Self-review by the recording proctor is blocked server-side; admins
 * bypass the lockout.
 *
 * @param {String} examId
 * @param {String} observationId
 * @param {Object} payload
 * @param {String} payload.reviewStatus   "accepted" | "dismissed"
 * @param {String} [payload.reviewComment]
 */
export const reviewExamObservation = async (
  examId,
  observationId,
  payload
) => {
  const res = await api.patch(
    `/exam/exams/${examId}/observations/id/${observationId}/review`,
    payload
  );
  return res.data;
};

/**
 * Teacher/admin: list ExamObservation rows for a student on a given
 * exam. Optional filters: type, reviewStatus, pagination.
 *
 * @param {String} examId
 * @param {String} studentId
 * @param {Object} [opts]
 * @param {String} [opts.type]          "General" | "Suspicious" | "Malpractice"
 * @param {String} [opts.reviewStatus]  "pending_review" | "accepted" | "dismissed"
 * @param {Number} [opts.limit=100]
 * @param {Number} [opts.offset=0]
 */
export const listExamObservations = async (examId, studentId, opts = {}) => {
  const params = {
    limit: opts.limit ?? 100,
    offset: opts.offset ?? 0,
  };
  if (opts.type) params.type = opts.type;
  if (opts.reviewStatus) params.reviewStatus = opts.reviewStatus;
  const res = await api.get(
    `/exam/exams/${examId}/observations/${studentId}`,
    { params }
  );
  return res.data;
};

/**
 * Teacher/admin: block a student mid-exam (Exam V2 spec §1.9.4 + §2.2.1).
 * Sets ExamSubmission.blocked + mirrors a `student_blocked` row into
 * the activity timeline. Idempotent — re-blocking refreshes the reason
 * fields but does NOT add a duplicate history row.
 *
 * @param {String} examId
 * @param {String} studentId
 * @param {Object} payload
 * @param {String[]} [payload.reasons]        — preset reason tags
 * @param {String}   [payload.customReason]   — free text (≤ 500 chars)
 *   At least one of reasons[] or customReason must be non-empty.
 */
export const blockExamStudent = async (examId, studentId, payload) => {
  const res = await api.post(
    `/exam/exams/${examId}/students/${studentId}/block`,
    payload
  );
  return res.data;
};

/**
 * Teacher/admin: unblock a student. Idempotent — unblocking an already-
 * unblocked submission returns success without writing extra history.
 *
 * @param {String} examId
 * @param {String} studentId
 */
export const unblockExamStudent = async (examId, studentId) => {
  const res = await api.post(
    `/exam/exams/${examId}/students/${studentId}/unblock`,
    {}
  );
  return res.data;
};

/**
 * Teacher/admin: grant the student additional minutes on the current
 * exam attempt (Exam V2 spec §2.2 item 11 + §5). Server enforces a
 * 60-minute single-grant cap and a 120-minute cumulative cap.
 *
 * @param {String} examId
 * @param {String} studentId
 * @param {Object} payload
 * @param {Number} payload.minutes      — positive integer ≤ 60
 * @param {String} [payload.reason]     — optional note (≤ 500 chars)
 * @returns {Promise<{success: boolean, extraTimeMinutes: number, grantedMinutes: number, effectiveDeadline: string, extensionCount: number}>}
 */
export const extendExamStudentTime = async (examId, studentId, payload) => {
  const res = await api.post(
    `/exam/exams/${examId}/students/${studentId}/extend-time`,
    payload
  );
  return res.data;
};

/**
 * Teacher/admin: fetch the compact proctor summary for a student
 * attempt (Exam V2 spec §5). Returns question counts (answered /
 * unanswered / marked-for-review / %), live time-spent seconds,
 * effective deadline (incl. Step 10 extra minutes), and a few
 * proctoring badges.
 *
 * @param {String} examId
 * @param {String} studentId
 */
export const getStudentExamSummary = async (examId, studentId) => {
  const res = await api.get(
    `/exam/exams/${examId}/students/${studentId}/summary`
  );
  return res.data;
};

/**
 * Teacher/admin: force-submit a student's in-progress attempt
 * (Exam V2 spec §2.2 "End Test"). Atomic — only an in_progress
 * attempt can be ended; concurrent student self-submit races
 * resolve deterministically on the submission's status flag.
 *
 * @param {String} examId
 * @param {String} studentId
 * @param {Object} [payload]
 * @param {String} [payload.reason]
 */
export const endExamStudentTest = async (examId, studentId, payload = {}) => {
  const res = await api.post(
    `/exam/exams/${examId}/students/${studentId}/end-test`,
    payload
  );
  return res.data;
};

/**
 * Teacher/admin: reopen a previously-ended student attempt (Exam V2
 * spec §5 "Allow proctor to reopen the exam"). Reverts status back
 * to in_progress and revives the ExamSession. Reason is REQUIRED
 * by the server.
 *
 * @param {String} examId
 * @param {String} studentId
 * @param {Object} payload
 * @param {String} payload.reason  — required, ≤ 500 chars
 */
export const reopenExamStudentTest = async (examId, studentId, payload) => {
  const res = await api.post(
    `/exam/exams/${examId}/students/${studentId}/reopen`,
    payload
  );
  return res.data;
};

/**
 * Teacher/admin: send a broadcast text message to one or more
 * students on an exam (Exam V2 spec §2.5.1). Empty/omitted
 * recipients targets every student on the exam.
 *
 * @param {String} examId
 * @param {Object} payload
 * @param {String} payload.message      — required, ≤ 1000 chars
 * @param {String[]} [payload.recipients] — User ObjectIds
 * @param {String[]} [payload.scheduleIds]
 */
export const sendExamBroadcast = async (examId, payload) => {
  const res = await api.post(`/exam/exams/${examId}/broadcast`, payload);
  return res.data;
};

/**
 * Teacher/admin: list recent broadcasts on an exam (newest-first).
 * Used by the drawer to show "recent broadcasts" below the Send form.
 *
 * @param {String} examId
 * @param {Object} [opts]
 * @param {Number} [opts.limit=50]
 * @param {Number} [opts.offset=0]
 */
export const listExamBroadcasts = async (examId, opts = {}) => {
  const params = {
    limit: opts.limit ?? 50,
    offset: opts.offset ?? 0,
  };
  const res = await api.get(`/exam/exams/${examId}/broadcasts`, { params });
  return res.data;
};

/**
 * Teacher/admin: compact exam-wide aggregate stats for the live
 * proctoring dashboard header strip (Exam V2 spec §2.0). Returns
 * counts rolled up across every submission/observation/broadcast
 * on the exam. Safe to poll; silent failure so a transient error
 * never blanks the dashboard header.
 *
 * @param {String} examId
 */
export const getExamProctorOverview = async (examId) => {
  try {
    const res = await api.get(`/exam/exams/${examId}/proctor-overview`);
    return res.data;
  } catch (_err) {
    return null;
  }
};

/**
 * Teacher/admin: exam-wide observations list for the review committee
 * inbox (Exam V2 spec §2.3). Returns every observation on the exam
 * with student + proctor names populated. Supports filter params:
 *   - type: "General" | "Suspicious" | "Malpractice"
 *   - reviewStatus: "pending_review" | "accepted" | "dismissed"
 *
 * @param {String} examId
 * @param {Object} [opts]
 * @param {String} [opts.type]
 * @param {String} [opts.reviewStatus]
 * @param {Number} [opts.limit=100]
 * @param {Number} [opts.offset=0]
 */
export const listAllExamObservations = async (examId, opts = {}) => {
  const params = {
    limit: opts.limit ?? 100,
    offset: opts.offset ?? 0,
  };
  if (opts.type) params.type = opts.type;
  if (opts.reviewStatus) params.reviewStatus = opts.reviewStatus;
  const res = await api.get(`/exam/exams/${examId}/observations`, {
    params,
  });
  return res.data;
};

/**
 * Teacher/admin: group notifications feed for the live proctoring
 * dashboard side panel (Exam V2 spec §2.5.2). Chronological stream
 * of notable events across every student on the exam. Safe to poll;
 * silent on failure so a transient error never blanks the panel.
 *
 * @param {String} examId
 * @param {Object} [opts]
 * @param {Number} [opts.limit=100]
 * @param {String} [opts.since]  — ISO timestamp; rows at or before are dropped
 */
export const getExamNotifications = async (examId, opts = {}) => {
  try {
    const params = { limit: opts.limit ?? 100 };
    if (opts.since) params.since = opts.since;
    const res = await api.get(`/exam/exams/${examId}/notifications`, {
      params,
    });
    return res.data;
  } catch (_err) {
    return null;
  }
};

/**
 * Teacher/admin: push a workspace setup guide category to a
 * specific student's exam UI (Exam V2 spec §2.2.2).
 *
 * @param {String} examId
 * @param {String} studentId
 * @param {Object} payload
 * @param {String} [payload.category="General"]
 * @param {String} [payload.note]
 */
export const pushWorkspaceSetupGuide = async (
  examId,
  studentId,
  payload = {}
) => {
  const res = await api.post(
    `/exam/exams/${examId}/students/${studentId}/push-workspace-setup`,
    payload
  );
  return res.data;
};

/**
 * Teacher/admin: record a face authentication decision for a student
 * (Exam V2 spec §2.4). Payload:
 *   { baselineUrl?, liveCaptureUrl?, idCaptureUrl?, similarityPercent?,
 *     decision: "authenticated" | "flagged" | "rejected", notes? }
 */
export const recordFaceAuthentication = async (
  examId,
  studentId,
  payload
) => {
  const res = await api.post(
    `/exam/exams/${examId}/students/${studentId}/face-authenticate`,
    payload
  );
  return res.data;
};

/**
 * Teacher/admin: list the face authentication history for a student
 * on an exam. Newest-first.
 */
export const listFaceAuthentications = async (examId, studentId) => {
  const res = await api.get(
    `/exam/exams/${examId}/students/${studentId}/face-authentications`
  );
  return res.data;
};

/**
 * Student-side: raise / lower the student's own hand on this exam
 * (Exam V2 spec §2.0). Silent on failure so the exam UI never
 * breaks if the request fails.
 *
 * @param {String} examId
 * @param {Object} [payload]
 * @param {Boolean} [payload.raised=true] — false to lower
 * @param {String} [payload.reason]
 */
// ── Live support during the exam ─────────────────────────────────────────
/** The candidate's panel: private thread, hand state, help tickets, unread. */
export const getExamSupport = async (examId) => {
  const res = await api.get(`/exam/exams/${examId}/support`);
  return res.data;
};
export const sendExamMessage = async (examId, body) => {
  const res = await api.post(`/exam/exams/${examId}/messages`, { body });
  return res.data;
};
/** scope: 'messages' (chat) or 'tickets' — marking one never marks the other. */
export const markExamMessagesRead = async (examId, scope = 'messages') => {
  const res = await api.post(`/exam/exams/${examId}/messages/read`, { scope });
  return res.data;
};
export const raiseExamHelpTicket = async (examId, payload) => {
  const res = await api.post(`/exam/exams/${examId}/help-tickets`, payload);
  return res.data;
};
export const replyExamHelpTicket = async (examId, ticketId, body) => {
  const res = await api.post(`/exam/exams/${examId}/help-tickets/${ticketId}/replies`, { body });
  return res.data;
};

export const raiseExamStudentHand = async (examId, payload = {}) => {
  try {
    const res = await api.post(`/exam/exams/${examId}/raise-hand`, payload);
    return res.data;
  } catch (_err) {
    return null;
  }
};

/**
 * Teacher/admin: lower a specific student's raised hand.
 */
export const lowerExamStudentHand = async (examId, studentId) => {
  const res = await api.post(
    `/exam/exams/${examId}/students/${studentId}/lower-hand`,
    {}
  );
  return res.data;
};

/**
 * Teacher/admin: escalate a pending observation to the chief
 * examiner (Exam V2 spec §2.11). Reason required. Once escalated,
 * only admin role can accept/dismiss via reviewExamObservation.
 *
 * @param {String} examId
 * @param {String} observationId
 * @param {Object} payload
 * @param {String} payload.reason — required, ≤ 1000 chars
 */
export const escalateExamObservation = async (
  examId,
  observationId,
  payload
) => {
  const res = await api.post(
    `/exam/exams/${examId}/observations/id/${observationId}/escalate`,
    payload
  );
  return res.data;
};

/**
 * Teacher/admin: request a face re-capture from a specific student
 * (Exam V2 spec §2.4, Phase 3 Step 24). Flips the student's
 * submission.faceCaptureRequest.status to "pending".
 */
export const requestExamFaceCapture = async (
  examId,
  studentId,
  payload = {}
) => {
  const res = await api.post(
    `/exam/exams/${examId}/students/${studentId}/request-face-capture`,
    payload
  );
  return res.data;
};

/**
 * Student-side: poll for a pending face capture request on this exam.
 * Silent failure — never breaks the exam UI.
 */
export const getMyExamFaceCaptureRequest = async (examId) => {
  try {
    const res = await api.get(
      `/exam/exams/${examId}/my-face-capture-request`
    );
    return res.data;
  } catch (_err) {
    return null;
  }
};

/**
 * 2.5 (d) — "start only when a proctor admits": the candidate's state at the
 * door (not_required | not_requested | waiting | admitted) and, when the exam
 * shows it, their proctor's name. Throws: the lobby treats a failure as "no
 * news", and the server still decides at Start.
 */
export const getMyAdmission = async (examId) => {
  const res = await api.get(`/exam/exams/${examId}/my-admission`);
  return res.data;
};

/**
 * Phase 4 — poll the caller's own block state so the BlockedOverlay appears
 * within one 15s tick of the proctor pressing Block/Unblock. Non-fatal.
 */
export const getMyExamBlockState = async (examId) => {
  try {
    const res = await api.get(`/exam/exams/${examId}/my-block-state`);
    return res.data;
  } catch (_err) {
    return null;
  }
};

/**
 * G16 — the step-out grace ran out. The server blocks this attempt (only a
 * proctor can lift it). Throws on failure: the screen keeps the paper paused
 * and reports again on its next poll.
 */
// 3.7 — the language the candidate reads the paper in, kept on the attempt
// for the marker. Never changes an answer.
export const setPaperLanguage = async (examId, lang) => {
  const res = await api.put(`/exam/exams/${examId}/paper-language`, { lang });
  return res.data;
};

export const reportStepOut = async (examId, { awaySeconds } = {}) => {
  const res = await api.post(`/exam/exams/${examId}/step-out`, { awaySeconds });
  return res.data;
};

/**
 * Teacher/admin: fetch the comprehensive Trust Report for a student
 * attempt (Exam V2 spec §2.12, Phase 7 Step 1). Rolls up every
 * Phase 3 signal — violations, observations, face auth, blocks,
 * time extensions, broadcasts — into a single payload with a
 * computed verdict.
 *
 * @param {String} examId
 * @param {String} studentId
 */
export const getStudentTrustReport = async (examId, studentId) => {
  const res = await api.get(
    `/exam/exams/${examId}/students/${studentId}/trust-report`
  );
  return res.data;
};

/**
 * 3.1 — mark a candidate's trust report reviewed (proctoring.trustReportRequired
 * holds the exam's results until every candidate's is). Throws. → { review }
 */
export const reviewTrustReport = async (examId, studentId, note = '') => {
  const res = await api.post(`/exam/exams/${examId}/students/${studentId}/trust-review`, { note });
  return res.data;
};

/**
 * Teacher/admin: manually mark a student's session as disconnected
 * (Exam V2 spec §2.0, Phase 3 Step 32). Idempotent.
 */
export const markExamStudentDisconnected = async (
  examId,
  studentId,
  payload = {}
) => {
  const res = await api.post(
    `/exam/exams/${examId}/students/${studentId}/mark-disconnected`,
    payload
  );
  return res.data;
};

/**
 * Teacher/admin: ping a student session to nudge them to reconnect.
 * Appends a proctor_pinged_student activity row.
 */
export const pingExamStudent = async (examId, studentId) => {
  const res = await api.post(
    `/exam/exams/${examId}/students/${studentId}/ping`,
    {}
  );
  return res.data;
};

/**
 * Teacher/admin: fetch the post-exam results roster (Phase 7 Step 2).
 * Returns paginated per-student result rows with a computed verdict
 * and filter query params:
 *   - status: "in_progress" | "submitted" | "graded" | "flagged"
 *   - verdict: "clear" | "review_recommended" | "high_risk"
 *   - q: free-text name/email/roll match
 *
 * @param {String} examId
 * @param {Object} [opts]
 */
export const getExamResultsRoster = async (examId, opts = {}) => {
  const params = {
    limit: opts.limit ?? 50,
    offset: opts.offset ?? 0,
  };
  if (opts.status) params.status = opts.status;
  if (opts.verdict) params.verdict = opts.verdict;
  if (opts.q) params.q = opts.q;
  const res = await api.get(`/exam/exams/${examId}/results-roster`, {
    params,
  });
  return res.data;
};

/**
 * Teacher/admin: download the results roster as a CSV file
 * (Exam V2 spec §7, Phase 7 Step 3). Uses the axios instance so
 * the auth header is attached, then triggers a browser download
 * via a synthesized blob URL.
 *
 * @param {String} examId
 * @param {Object} [opts] — same filter shape as getExamResultsRoster
 */
/**
 * Teacher/admin: bulk score adjustment on selected submissions
 * (Exam V2 spec §7, Phase 7 Step 4).
 *
 * @param {String} examId
 * @param {Object} payload
 * @param {String[]} payload.submissionIds
 * @param {"delta"|"absolute"} payload.mode
 * @param {Number} payload.value
 * @param {String} payload.reason
 */
export const bulkAdjustExamScores = async (examId, payload) => {
  const res = await api.post(
    `/exam/exams/${examId}/bulk-score-adjust`,
    payload
  );
  return res.data;
};

// Phase 7 Step 7 — Rubric-based subjective grading.
/**
 * Grade a single subjective answer using a rubric's criteria.
 * Works for all three rubric types: weighted, checklist, rating_scale.
 *
 * @param {String} examId
 * @param {String} subId        submission ID
 * @param {Object} payload
 *   .questionIndex       which answer to grade
 *   .rubricId?           optional — saved AIRubric template
 *   .criteriaScores[]    per-criterion scored array
 *   .overallComment?     optional free-text
 * @returns {Promise<{success, pointsAwarded, totalScore, ...}>}
 */
export const gradeAnswerWithRubric = async (examId, subId, payload) => {
  const res = await api.post(
    `/exam/exams/${examId}/submissions/${subId}/grade-rubric`,
    payload
  );
  return res.data;
};

// Phase 7 Step 8 — AI DRAFT rubric grade (human-in-the-loop).
/**
 * Ask the server's open-LLM stack (Workers AI → Groq, ≈₹0) to draft a
 * per-criterion score for one subjective answer against a saved rubric.
 * Persists nothing — the teacher reviews/edits the draft in RubricGradingForm
 * then submits it via gradeAnswerWithRubric.
 *
 * @param {String} examId
 * @param {String} subId          submission ID
 * @param {Object} payload
 *   .questionIndex   which answer to draft-grade
 *   .rubricId        saved AIRubric template (required)
 * @returns {Promise<{success, aiGenerated, draft:{criteriaScores, overallComment, rubricType}}>}
 */
export const aiDraftRubricGrade = async (examId, subId, payload) => {
  const res = await api.post(
    `/exam/exams/${examId}/submissions/${subId}/ai-draft-rubric`,
    payload
  );
  return res.data;
};

// List saved AI rubrics (for the grading rubric picker). Uses the shared `api`
// instance so the backend base-URL is honoured in every environment (a raw
// relative fetch would hit the frontend origin and 404 in split-origin dev).
export const getAiRubrics = async () => {
  const res = await api.get('/ai-rubrics');
  return res.data;
};

// Phase 7 Step 6 — Admin result publishing workflow.
export const publishExamResults = async (examId, note = "") => {
  const res = await api.post(`/exam/exams/${examId}/publish-results`, { note });
  return res.data;
};

export const unpublishExamResults = async (examId, note = "") => {
  const res = await api.post(`/exam/exams/${examId}/unpublish-results`, {
    note,
  });
  return res.data;
};

export const downloadExamResultsRosterCsv = async (examId, opts = {}) => {
  const params = {};
  if (opts.status) params.status = opts.status;
  if (opts.verdict) params.verdict = opts.verdict;
  if (opts.q) params.q = opts.q;
  const res = await api.get(`/exam/exams/${examId}/results-roster.csv`, {
    params,
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([res.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `results-${examId}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return { success: true };
};

/**
 * Student-side: fetch broadcasts addressed to me for this exam
 * (Exam V2 spec §1.9.5). Matches "all-students" broadcasts and
 * any targeted at me. Safe to poll; silent on failure so a
 * transient network error never disrupts the exam UI.
 *
 * @param {String} examId
 * @param {Object} [opts]
 * @param {Number} [opts.limit=20]
 * @returns {Promise<null | {success: boolean, total: number, unreadCount: number, broadcasts: Array}>}
 */
export const getMyExamBroadcasts = async (examId, opts = {}) => {
  try {
    const params = { limit: opts.limit ?? 20 };
    const res = await api.get(
      `/exam/exams/${examId}/my-broadcasts`,
      { params }
    );
    return res.data;
  } catch (_err) {
    // Fire-and-forget — never break the student UI over this.
    return null;
  }
};

/**
 * Student-side: acknowledge a broadcast as read (Exam V2 spec §2.5.1).
 * Idempotent — a second call is a no-op. Silent on failure.
 *
 * @param {String} broadcastId
 */
export const markExamBroadcastRead = async (broadcastId) => {
  try {
    const res = await api.post(`/exam/broadcasts/${broadcastId}/read`, {});
    return res.data;
  } catch (_err) {
    return null;
  }
};

/**
 * Teacher/admin: read the unified activity timeline for a student
 * (Exam V2 spec §2.6). Supports pagination + prefix/severity/eventTypes
 * filters for the User Activity modal.
 *
 * @param {String} examId
 * @param {String} studentId   — Student ObjectId
 * @param {Object} [opts]
 * @param {Number} [opts.limit=100]
 * @param {Number} [opts.offset=0]
 * @param {String} [opts.prefix]      e.g. "violation_"
 * @param {String} [opts.severity]    e.g. "warning"
 * @param {String[]} [opts.eventTypes]
 */
export const getStudentActivityLog = async (examId, studentId, opts = {}) => {
  const params = {
    limit: opts.limit ?? 100,
    offset: opts.offset ?? 0,
  };
  if (opts.prefix) params.prefix = opts.prefix;
  if (opts.severity) params.severity = opts.severity;
  if (opts.eventTypes && opts.eventTypes.length) {
    params.eventTypes = opts.eventTypes.join(",");
  }
  const res = await api.get(
    `/exam/exams/${examId}/activity/${studentId}`,
    { params }
  );
  return res.data;
};

// ── Phase 5: Proctor namespace endpoints ──

/** Get list of exams the current user can proctor (§2.0 welcome page). */
export const getProctorAvailableTests = async (params = {}) => {
  const res = await api.get("/exam-proctor/available-tests", { params });
  return res.data;
};

/** Polling-optimised grid tile payload for the proctor dashboard. */
export const getProctorGrid = async (examId) => {
  const res = await api.get(`/exam-proctor/${examId}/grid`);
  return res.data;
};

/**
 * "Start only when a proctor admits" (2.5 d): let waiting candidates in — the
 * route the console grid uses. `{ studentIds }` or `{ all: true }` (everyone
 * waiting that this proctor may admit — a proctor: their own). Throws.
 */
export const admitWaitingCandidates = async (examId, body) => {
  const res = await api.post(`/exam-proctor/${examId}/admissions/admit`, body);
  return res.data;
};

/**
 * A QR for photographing worksheet pages with the phone (S03): the token names
 * the question the pages are for, and `label` is what the phone shows for it
 * ("Section B · Question 2" — never the question itself). Resolves { token,
 * uploadUrl, qrSvg, expiresIn }.
 */
// lng — the language the phone's page opens in (W4.2); the server keeps it
// only when the institution has enabled it for exam screens.
export const generateMobilePairToken = async (examId, { questionId, label = '', lng } = {}) => {
  const res = await api.post(`/exam-secondary-device/${examId}/pair`, { questionId, label, ...(lng ? { lng } : {}) });
  return res.data;
};

/** Upload audio answer recording (Phase 4). */
export const uploadAudioAnswer = async (examId, questionId, file, durationMs) => {
  const form = new FormData();
  form.append("file", file);
  form.append("questionId", questionId);
  if (durationMs) form.append("durationMs", String(durationMs));
  const res = await api.post(`/exam/exams/${examId}/upload-audio`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

/**
 * A file-upload answer: the file itself (QB-24). Resolves { file: { url, name,
 * size, mimeType } } — the url comes back signed for this session; the answer
 * stores it without the signature. Throws on failure (the caller shows it and
 * offers a retry). onProgress gets 0–100 while the bytes go up.
 */
export const uploadAnswerFile = async (examId, questionId, file, onProgress) => {
  const form = new FormData();
  form.append("questionId", questionId);
  form.append("file", file);
  const res = await api.post(`/exam/exams/${examId}/answer-file`, form, {
    headers: { "Content-Type": "multipart/form-data" },
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded * 100) / e.total));
    },
  });
  return res.data;
};

/** Upload worksheet page image (Phase 4). */
export const uploadWorksheetPage = async (examId, questionId, file, pageNum = 1, sourceDevice = "desktop") => {
  const form = new FormData();
  form.append("file", file);
  form.append("questionId", questionId);
  form.append("pageNum", String(pageNum));
  form.append("sourceDevice", sourceDevice);
  const res = await api.post(`/exam/exams/${examId}/upload-worksheet`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

/** Slice 5.4 — student's own worksheet uploads (restore after resume). */
export const getMyWorksheets = async (examId) => {
  const res = await api.get(`/exam/exams/${examId}/my-worksheets`);
  return res.data;
};

/** Slice 6.1 — run code against the question's VISIBLE test cases (EXAM_V3_LAB). */
export const runCodeTests = async (examId, questionId, code) => {
  const res = await api.post(`/exam-lab/exams/${examId}/questions/${questionId}/run`, { code });
  return res.data;
};

/** C22 — run code once on the candidate's own input: stdout, stderr, time. No mark. */
export const runCustomInput = async (examId, questionId, code, stdin) => {
  const res = await api.post(`/exam-lab/exams/${examId}/questions/${questionId}/run-custom`, { code, stdin });
  return res.data;
};

/** Capture face baseline (Phase 4 — pre-exam). */
export const captureFaceBaseline = async (examId, file) => {
  const form = new FormData();
  form.append("file", file);
  const res = await api.post(`/exam/exams/${examId}/capture-face-baseline`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

/**
 * A live face snapshot (P33) — a real webcam frame. Stored on the attempt and,
 * when a proctor's re-capture request is pending, it answers that request.
 */
export const captureFaceLive = async (examId, file) => {
  const form = new FormData();
  form.append("file", file, "live.jpg");
  const res = await api.post(`/exam/exams/${examId}/capture-face-live`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

/** Capture ID card (Phase 4). `via`: "webcam" | "upload" (P32 — the proctor sees which). */
export const captureIDCard = async (examId, file, via = "webcam") => {
  const form = new FormData();
  form.append("file", file, via === "upload" ? "id-upload.jpg" : "id-webcam.jpg");
  form.append("via", via);
  const res = await api.post(`/exam/exams/${examId}/capture-idcard`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

/** Exam V3 §7 — pre-exam voice sample (capture-only, flag EXAM_V3_PROCTOR). */
export const captureVoiceSample = async (examId, file) => {
  const form = new FormData();
  form.append("file", file);
  const res = await api.post(`/exam/exams/${examId}/capture-voice`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return res.data;
};

/** Exam V3 feature flags (backend is the single source of truth). */
export const getExamV3Flags = async () => {
  const res = await api.get(`/exam-v3/flags`);
  return res.data;
};

/**
 * Student's own trust report — opens the PDF in a new tab. Uses the api
 * client (so the Authorization header is attached); a raw window.open of the
 * URL would send no token and 401. Falls back to a JSON blob when Puppeteer
 * is unavailable server-side.
 */
export const openMyTrustReport = async (examId) => {
  const res = await api.get(`/exam-reports/my-trust-report/${examId}/pdf`, {
    responseType: 'blob',
  });
  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
  window.open(url, '_blank');
  // Revoke a little later so the new tab has time to load the object URL.
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};
